import Box from '@mui/material/Box';
import { FC } from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton, useMediaQuery } from '@mui/material';
import Typography from '@mui/material/Typography';
import {
  getCurrentDate,
  getDateOfDay,
  getNextDayName,
  getPreviousDayName,
  isDateWithinThreeDays,
  isTomorrowSelectedDate,
} from '../../../../utils/format';
import { Programme } from '../../services/type';
import { getRadientBG } from '../../../../utils/display';

interface Props {
  selectedDate: string;
  handleDateChange: (value: string) => void;
  program?: Programme;
}

const CourseHeader: FC<Props> = ({
  selectedDate,
  handleDateChange,
  program,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const totalCourses = program?.reunions?.reduce(
    (acc, reunion) => acc + reunion.courses.length,
    0
  );

  const handleDecrementDate = () => {
    const day = parseInt(selectedDate.slice(0, 2), 10);
    const month = parseInt(selectedDate.slice(2, 4), 10) - 1; // Months are 0-indexed
    const year = parseInt(selectedDate.slice(4, 8), 10);

    const date = new Date(year, month, day);

    date.setDate(date.getDate() - 1);

    const newDay = date.getDate().toString().padStart(2, '0'); // Ensure two digits
    const newMonth = (date.getMonth() + 1).toString().padStart(2, '0'); // Add 1 since months are 0-indexed
    const newYear = date.getFullYear();

    handleDateChange(`${newDay}${newMonth}${newYear}`);
  };

  const handleIncrementDate = () => {
    const day = parseInt(selectedDate.slice(0, 2), 10);
    const month = parseInt(selectedDate.slice(2, 4), 10) - 1; // Months are 0-indexed
    const year = parseInt(selectedDate.slice(4, 8), 10);

    const date = new Date(year, month, day);

    date.setDate(date.getDate() + 1);

    const newDay = date.getDate().toString().padStart(2, '0'); // Ensure two digits
    const newMonth = (date.getMonth() + 1).toString().padStart(2, '0'); // Add 1 since months are 0-indexed
    const newYear = date.getFullYear();

    handleDateChange(`${newDay}${newMonth}${newYear}`);
  };

  const disabledIncrement = isTomorrowSelectedDate(selectedDate);
  const disabledDecrement = isDateWithinThreeDays(selectedDate);

  const handleClickToday = () => {
    handleDateChange(getCurrentDate())
  }

  const subtitle = program ? (
    <Box
      px={1}
      display="flex"
      justifyContent="center"
      gap={2}
      color={'white'}
    >
      <Typography variant="subtitle2" >
        {isMobile ? `🏇 ${totalCourses}` : `🏇 ${totalCourses} courses`}
      </Typography>
      <Typography variant="subtitle2">
        {isMobile ? `📍 ${program?.reunions?.length}` : `📍 ${program?.reunions?.length} réunions`}
      </Typography>
    </Box>
  ) : null;

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: isMobile ? 0 : 3,
        border: isMobile ? '' : '2px solid',
        borderColor: 'primary.main',
        background: getRadientBG(),
        boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
        height: isMobile ? "22vh" : "17vh"
      }}
    >
      {/* HEADER */}
      <Box
        px={2}
        py={1}
        borderRadius={isMobile ? 0 : "10px 10px 0px 0px"}
        display="flex"
        flexDirection="row"
        alignItems="center"
        gap={1}
        borderColor="primary.main"
        bgcolor="primary.main"
        color="white"
      >
        <CalendarMonthIcon />

        <Typography variant={"h6"} color="white" fontWeight={700}>
          Date
        </Typography>

        {/* spacer */}
        <Box flex={1} />

        {/* BOUTON AUJOURD'HUI */}
        <Typography
          onClick={handleClickToday}
          sx={{
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.15)',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.25)',
            },
          }}
        >
          Aujourd’hui
        </Typography>
      </Box>


      {/* CONTENU */}
      <Box
        px={1}
        py={0.5}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <IconButton
          onClick={handleDecrementDate}
          disabled={disabledDecrement}
          sx={{
            width: '20%',
            bgcolor: "secondary.main",
            color: "white",
            borderRadius: 2,
            '&:hover': { bgcolor: 'primary.light' },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 16 }} />
          <Typography fontSize={isMobile ? 11 : 13} fontWeight={700}>
            {getPreviousDayName(selectedDate)}
          </Typography>
        </IconButton>

        <Box display="flex" alignItems="center" gap={0.5} flexDirection={'column'}>
          <Typography
            variant={"h6"}
            color="white"
            fontWeight={700}
          >
            {getDateOfDay(selectedDate)}
          </Typography>
          {subtitle}
        </Box>

        <IconButton
          onClick={handleIncrementDate}
          disabled={disabledIncrement}
          sx={{
            width: '20%',
            bgcolor: "secondary.dark",
            color: "white",
            borderRadius: 2,
            '&:hover': { bgcolor: 'primary.light' },
          }}
        >
          <Typography fontSize={isMobile ? 11 : 13} fontWeight={700}>
            {getNextDayName(selectedDate)}
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

export default CourseHeader;
